/**
 * CreateChallengeModal — coach picks exactly 5 scenarios and gives the
 * challenge a name. Opens from the IQ Simulator hub. After creation the
 * coach gets a ready-to-share URL + native share sheet via WhatsApp/etc.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';
import { X, Check, Loader2, Sparkles, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateChallengeModal({ open, onClose, onCreated }) {
  const { t, lang } = useLang();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [picked, setPicked] = useState([]); // array of scenario ids (max 5)
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // {code, share_path}
  const [filter, setFilter] = useState('all'); // all | offensive | defensive | complete

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/iq/scenarios?lang=${encodeURIComponent(lang || 'es')}`)
      .then(r => setScenarios(r.data.scenarios || []))
      .catch(() => setScenarios([]))
      .finally(() => setLoading(false));
  }, [open, lang]);

  // Reset state every time the modal opens fresh.
  useEffect(() => {
    if (open) {
      setTitle('');
      setPicked([]);
      setCreated(null);
      setFilter('all');
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (filter === 'all') return scenarios;
    return scenarios.filter(s => s.category === filter);
  }, [scenarios, filter]);

  const togglePick = (id) => {
    setPicked(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 5) {
        toast.error(t('iqc_max_5'));
        return prev;
      }
      return [...prev, id];
    });
  };

  const canSubmit = title.trim().length >= 2 && picked.length === 5 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/iq/challenges', {
        title: title.trim(),
        scenario_ids: picked,
      });
      setCreated(data);
      onCreated?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const shareUrl = created
    ? `${window.location.origin}${created.share_path}`
    : '';

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('ujp_link_copied'));
    } catch {
      toast.error(t('ujp_copy_failed'));
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyShare(); return; }
    try {
      await navigator.share({
        title: title || t('iqc_share_default_title'),
        text: t('iqc_share_default_text', { title: title || '' }),
        url: shareUrl,
      });
    } catch {/* user cancelled */}
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={() => { if (!created) onClose(); }}
      data-testid="iqc-create-modal"
    >
      <div
        className="bg-navy-deep border-2 border-gold/60 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold">{t('iqc_module_label')}</div>
            <h2 className="font-h uppercase text-2xl text-white">{t('iqc_create_h')}</h2>
          </div>
          <button onClick={onClose} className="text-gold hover:text-alert p-1" data-testid="iqc-create-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* ---------- Result view (after creation) ---------- */}
        {created ? (
          <div className="overflow-auto" data-testid="iqc-create-done">
            <div className="rounded-lg border-2 border-gold/60 bg-gold/10 p-5 mb-4 text-center">
              <Sparkles className="w-8 h-8 text-gold mx-auto mb-2" />
              <div className="font-h uppercase text-xl text-white mb-1">{t('iqc_create_success')}</div>
              <div className="text-sm text-gray-300 mb-3">{t('iqc_create_success_sub')}</div>
              <div className="bg-navy-deep border border-gold/30 rounded px-3 py-2 font-mono text-xs text-gold break-all">
                {shareUrl}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={nativeShare}
                data-testid="iqc-create-share-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm font-semibold hover:bg-gold-light"
              >
                <Share2 className="w-4 h-4" /> {t('ujp_share')}
              </button>
              <button
                onClick={copyShare}
                data-testid="iqc-create-copy-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm font-h uppercase tracking-wider"
              >
                <Copy className="w-4 h-4" /> {t('ujp_copy')}
              </button>
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-600 text-gray-300 hover:border-gold/60 hover:text-gold text-sm font-h uppercase tracking-wider"
              >
                {t('close')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Title input */}
            <div className="mb-3 flex-shrink-0">
              <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 block mb-1">
                {t('iqc_create_title_label')}
              </label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t('iqc_create_title_placeholder')}
                maxLength={80}
                data-testid="iqc-create-title-input"
                className="w-full bg-card-steel border border-gold/30 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
              />
            </div>

            {/* Filter pills */}
            <div className="flex gap-1.5 mb-3 flex-shrink-0 flex-wrap">
              {['all', 'offensive', 'defensive', 'complete'].map(c => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`iqc-create-filter-${c}`}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-h uppercase tracking-wider border ${
                    filter === c ? 'bg-gold text-navy border-gold' : 'border-gold/30 text-gold hover:bg-gold/10'
                  }`}
                >
                  {c === 'all' ? t('iq_lb_all')
                    : c === 'offensive' ? t('iq_cat_offensive')
                    : c === 'defensive' ? t('iq_cat_defensive')
                    : t('iq_cat_complete')}
                </button>
              ))}
              <div className="ms-auto text-[11px] font-mono uppercase tracking-wider text-gold/80">
                {picked.length} / 5
              </div>
            </div>

            {/* Scrollable scenario list */}
            <div className="flex-1 overflow-auto pr-1 mb-3 -mr-1" data-testid="iqc-create-list">
              {loading ? (
                <div className="flex items-center justify-center py-10 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center text-gray-500 py-8 text-sm">{t('iq_no_scenarios')}</div>
              ) : (
                <div className="space-y-1.5">
                  {filtered.map(sc => {
                    const isPicked = picked.includes(sc.id);
                    const idx = picked.indexOf(sc.id);
                    return (
                      <button
                        key={sc.id}
                        onClick={() => togglePick(sc.id)}
                        data-testid={`iqc-create-pick-${sc.id}`}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-md border transition ${
                          isPicked
                            ? 'border-gold bg-gold/10'
                            : 'border-gold/20 hover:border-gold/60 hover:bg-gold/5'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isPicked ? 'border-gold bg-gold text-navy' : 'border-gold/40 text-transparent'
                        }`}>
                          {isPicked ? <span className="text-[11px] font-bold">{idx + 1}</span> : <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-h uppercase text-xs tracking-wider text-white truncate">{sc.title}</div>
                          <div className="text-[10px] font-mono text-gray-500 uppercase">{sc.category} · {sc.difficulty}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-gold/15 flex-shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md border border-gray-600 text-gray-300 hover:border-gold/60 hover:text-gold font-h uppercase text-xs tracking-wider"
              >
                {t('cancel')}
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit}
                data-testid="iqc-create-submit"
                className="px-5 py-2 rounded-md bg-gold text-navy font-h uppercase text-sm tracking-wider font-semibold disabled:opacity-40 hover:bg-gold-light inline-flex items-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('iqc_create_submit')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
